import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  User,
  Building,
  Mail,
  Bell,
  Shield,
  Key,
  Trash2,
  Save,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings({ user, logout, token }) {
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    organization: user?.organization || ""
  });
  
  const [notifications, setNotifications] = useState({
    email_scan_complete: true,
    email_critical_vuln: true,
    email_compliance_change: false,
    email_weekly_summary: true
  });

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      // Simulated save - in production would call API
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold font-[Chivo]">Settings</h2>
          <p className="text-muted-foreground">
            Manage your account and notification preferences
          </p>
        </div>

        {/* Profile Settings */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-[Chivo] flex items-center gap-2">
              <User className="h-5 w-5" />
              Profile Information
            </CardTitle>
            <CardDescription>
              Update your personal and organization details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                data-testid="settings-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                disabled
                className="opacity-50"
                data-testid="settings-email-input"
              />
              <p className="text-xs text-muted-foreground">
                Email cannot be changed. Contact support if needed.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="organization">Organization</Label>
              <Input
                id="organization"
                value={profileData.organization}
                onChange={(e) => setProfileData({ ...profileData, organization: e.target.value })}
                placeholder="Your healthcare organization"
                data-testid="settings-org-input"
              />
            </div>

            <Button 
              onClick={handleSaveProfile} 
              disabled={loading}
              data-testid="save-profile-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-[Chivo] flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notification Preferences
            </CardTitle>
            <CardDescription>
              Choose what notifications you receive
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Scan Completion Alerts</Label>
                <p className="text-sm text-muted-foreground">
                  Receive email when a security scan completes
                </p>
              </div>
              <Switch
                checked={notifications.email_scan_complete}
                onCheckedChange={(checked) => setNotifications({ ...notifications, email_scan_complete: checked })}
                data-testid="notify-scan-complete"
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Critical Vulnerability Alerts</Label>
                <p className="text-sm text-muted-foreground">
                  Immediate notification for critical findings
                </p>
              </div>
              <Switch
                checked={notifications.email_critical_vuln}
                onCheckedChange={(checked) => setNotifications({ ...notifications, email_critical_vuln: checked })}
                data-testid="notify-critical-vuln"
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Compliance Status Changes</Label>
                <p className="text-sm text-muted-foreground">
                  Alert when compliance status changes
                </p>
              </div>
              <Switch
                checked={notifications.email_compliance_change}
                onCheckedChange={(checked) => setNotifications({ ...notifications, email_compliance_change: checked })}
                data-testid="notify-compliance-change"
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Weekly Security Summary</Label>
                <p className="text-sm text-muted-foreground">
                  Weekly digest of security status and findings
                </p>
              </div>
              <Switch
                checked={notifications.email_weekly_summary}
                onCheckedChange={(checked) => setNotifications({ ...notifications, email_weekly_summary: checked })}
                data-testid="notify-weekly-summary"
              />
            </div>
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-[Chivo] flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Security
            </CardTitle>
            <CardDescription>
              Manage your account security settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border border-border rounded-sm">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-sm bg-primary/10 flex items-center justify-center">
                  <Key className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Change Password</p>
                  <p className="text-sm text-muted-foreground">Update your account password</p>
                </div>
              </div>
              <Button variant="outline" data-testid="change-password-btn">
                Change
              </Button>
            </div>
            
            <div className="flex items-center justify-between p-4 border border-border rounded-sm">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-sm bg-emerald-500/10 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="font-medium">Two-Factor Authentication</p>
                  <p className="text-sm text-muted-foreground">Add an extra layer of security</p>
                </div>
              </div>
              <Button variant="outline" data-testid="enable-2fa-btn">
                Enable
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* API Keys */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-[Chivo] flex items-center gap-2">
              <Key className="h-5 w-5" />
              API Access
            </CardTitle>
            <CardDescription>
              Manage API keys for programmatic access
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 border border-dashed border-border rounded-sm text-center">
              <Key className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground mb-3">
                API access is available on Professional and Enterprise plans
              </p>
              <Button variant="outline" size="sm" data-testid="upgrade-plan-btn">
                Upgrade Plan
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="bg-card border-red-500/20">
          <CardHeader>
            <CardTitle className="font-[Chivo] flex items-center gap-2 text-red-500">
              <Trash2 className="h-5 w-5" />
              Danger Zone
            </CardTitle>
            <CardDescription>
              Irreversible actions for your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 border border-red-500/20 rounded-sm bg-red-500/5">
              <div>
                <p className="font-medium text-red-500">Delete Account</p>
                <p className="text-sm text-muted-foreground">
                  Permanently delete your account and all associated data
                </p>
              </div>
              <Button variant="destructive" data-testid="delete-account-btn">
                Delete Account
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
